import { addToKeyring, keypairFromSecret, openKeyring, sealKeyring, type KeyringEntry } from "@timecapsule/crypto";
import { api } from "./api";

const getPublicKeyHex = (secretKey: Uint8Array) => keypairFromSecret(secretKey).publicKey;

/**
 * The keyring in the browser (PLAN.md 4.16 K4): earlier private keys, opened with the current key
 * and kept in memory only while that key is; Lock forgets them (forgetOldKeys).
 */
let cache: { pubkey: string; keys: Map<string, Uint8Array>; locked: boolean } | null = null;

/**
 * The stored keyring exists but none of the given keys opens it (PLAN.md 4.18 step 4): it is sealed
 * to another key, for example after a key reset that stopped before the keyring moved. Nothing was
 * changed; adding that key's phrase moves it (addOldKeys).
 */
export class KeyringLockedError extends Error {
  constructor() {
    super("the keyring is sealed to another key");
    this.name = "KeyringLockedError";
  }
}

/** The stored keyring, opened with the first of `keys` that can. A network error is thrown as is. */
async function load(keys: Uint8Array[]): Promise<{ version: number; entries: KeyringEntry[] }> {
  const row = await api.keyring();
  if (!row.blob) return { version: row.version, entries: [] };
  for (const key of keys) {
    try {
      return { version: row.version, entries: openKeyring(row.blob, key) };
    } catch {
      // Sealed to another key, or unreadable: try the next one, never start over.
    }
  }
  throw new KeyringLockedError();
}

/** Earlier keys by pubkey; empty when there is no keyring or it cannot be opened with this key. */
export async function oldKeys(secretKey: Uint8Array): Promise<Map<string, Uint8Array>> {
  const pubkey = getPublicKeyHex(secretKey);
  if (cache?.pubkey === pubkey) return cache.keys;
  let keys = new Map<string, Uint8Array>();
  let locked = false;
  try {
    keys = new Map((await load([secretKey])).entries.map((e) => [e.pubkey, e.secretKey]));
  } catch (err) {
    locked = err instanceof KeyringLockedError;
    console.warn("keyring not opened", err);
    // A network error is not remembered: the next call tries again.
    if (!locked) return keys;
  }
  cache = { pubkey, keys, locked };
  return keys;
}

/** True when a keyring is stored but the current key cannot open it (an unfinished reset). */
export async function keyringLocked(secretKey: Uint8Array): Promise<boolean> {
  await oldKeys(secretKey);
  return cache?.pubkey === getPublicKeyHex(secretKey) && cache.locked;
}

export function forgetOldKeys(): void {
  cache = null;
}

/** The key that opens a capsule to `recipient`: the current one, or an earlier one from the keyring. */
export async function keyFor(recipient: string | null, secretKey: Uint8Array): Promise<Uint8Array> {
  if (!recipient || recipient === getPublicKeyHex(secretKey)) return secretKey;
  return (await oldKeys(secretKey)).get(recipient) ?? secretKey;
}

/**
 * Adds earlier keys to the keyring sealed to `secretKey` (the current key). A keyring still sealed
 * to one of the added keys (a reset that stopped before the keyring moved) is opened with it and
 * moves to the current key with everything it held.
 */
export async function addOldKeys(secretKey: Uint8Array, add: Uint8Array[]): Promise<void> {
  const { version, entries } = await load([secretKey, ...add]);
  await api.saveKeyring(version, sealKeyring(addToKeyring(entries, add, getPublicKeyHex(secretKey)), secretKey));
  forgetOldKeys();
}

/**
 * After a key reset: the keyring sealed to the old key moves to the new key, with the old key added
 * when the member chose to keep it (K4). Never starts over (PLAN.md 4.18 step 4): a network error is
 * thrown and the call can be repeated; a repeat after a move that succeeded opens it with the new
 * key; a keyring neither key opens is left as it is (KeyringLockedError).
 */
export async function carryKeyring(oldSecret: Uint8Array, newSecret: Uint8Array, keepOld: boolean): Promise<void> {
  const { version, entries: carried } = await load([oldSecret, newSecret]);
  const entries = addToKeyring(carried, keepOld ? [oldSecret] : [], getPublicKeyHex(newSecret));
  if (entries.length === 0 && version === 0) return;
  await api.saveKeyring(version, sealKeyring(entries, newSecret));
  forgetOldKeys();
}
